import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

// Landing for "kiipeilyteline pihalle" (110/mo). We DON'T sell outdoor
// climbing frames — but the search intent is often "we live in a small
// Finnish apartment, we want indoor climbing that works like an
// outdoor one". Positioning: our indoor Pikler set = an outdoor
// climbing frame's exercise value, without the yard.
const SLUG = "kiipeilyteline-pihalle";
const H1 = "Kiipeilyteline pihalle vai sisälle? Vertailu suomalaiseen kotiin";
const TITLE = "Kiipeilyteline pihalle vai sisälle — vertailu | Montessori24";
const DESC =
  "Kiipeilyteline pihalle vs. sisälle: säänkesto, hinta, käyttöikä ja tila. Miten Pikler-sisäteline korvaa pihatelineen suomalaisessa talvessa.";

const FAQS = [
  {
    q: "Kannattaako kiipeilyteline ostaa pihalle vai sisälle Suomessa?",
    a: "Suomessa pihateline on käytössä toukokuusta syyskuuhun — noin 5 kuukautta. Loput 7 kuukautta se on lumen alla tai märkänä. Sisäteline on käytössä 12 kk vuodessa, siirrettävissä huoneesta toiseen ja huomattavasti helpompi säilyttää. Karkeamotorinen harjoitus on sama.",
  },
  {
    q: "Mistä materiaalista pihalle sopiva kiipeilyteline pitää olla?",
    a: "Ulkokäyttöön tarvitaan painekyllästetty puu, ruostumaton tai kuumasinkitty metalli tai HDPE-muovi. Tavallinen koivuvaneri (kuten meidän sisätelineissä) ei kestä ympärivuotista säänvaihtelua ilman vuosittaista lakkausta — käyttöikä lyhenee 8–10 vuodesta 2–3 vuoteen.",
  },
  {
    q: "Paljonko pihakiipeilyteline maksaa verrattuna sisätelineeseen?",
    a: "Kunnollinen pihakiipeilyteline (esim. lelupiste, hyvähetki, Puuilo, isot Woodstockin mallit) maksaa 400–1 500 €. Perusasennus vaatii tasoitetun pohjan ja usein turva-alustan (kumirouhe, hakemurske) joka on 15–30 €/m². Meidän Pikler-sisäteline maksaa 149–299 € ja tarvitsee vain pehmeän maton alle.",
  },
  {
    q: "Voiko sisätelineen viedä pihalle kesällä?",
    a: "Lyhyeksi ajaksi (esim. terassille aurinkoisena päivänä) kyllä. Yön yli tai sadeviikkoina ei suositella — koivuvaneri turpoaa kosteudessa ja pinta halkeaa. Jos etsit teline pihalle pysyvästi, hanki oikea säänkestävä ulkomalli.",
  },
  {
    q: "Mikä on paras sisäkiipeilyteline pienessä kerrostaloasunnossa?",
    a: "Taittuva Pikler-kolmio + kaari on paras yhdistelmä pieneen asuntoon: 80×80 cm käytössä, taittuu tasoksi säilytystä varten sängyn alle. Ramppi lisättynä syntyy pieni liukumäki — sama karkeamotorinen harjoitus kuin pihatelineessä ilman pihan tarvetta.",
  },
];

export const Route = createFileRoute("/kiipeilyteline-pihalle")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: KiipeilytelinePihallePage,
});

function KiipeilytelinePihallePage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Kiipeilyteline pihalle vs. sisälle"
      title={H1}
      intro="Etsitkö kiipeilyteline pihalle? Suomen ilmastossa pihateline on käytössä 5 kk, sisäteline 12 kk. Vertailemme tässä molemmat vaihtoehdot — säänkesto, hinta, tila ja käyttöikä — ja näytämme miten Pikler-sisäteline korvaa pihakiipeilytelineen suomalaisessa kodissa."
      kauppaCategory="climbing"
      match={(t) => /kiipeil|kolmio|kaari|kolmikulm|ramp|climbing|climber|triangle|arch|pikler/i.test(t)}
      relatedChips={[
        { label: "Kiipeilyteline sisälle", to: "/kiipeilyteline" },
        { label: "Pikler-kolmio", to: "/pikler-kolmio" },
        { label: "Kiipeilykolmio-opas (ikä)", to: "/montessori-koulu/kiipeilykolmio-lapselle-ikaopas" },
        { label: "Pikler-opas", to: "/montessori-koulu/pikler-kiipeilyteline-opas" },
        { label: "Kaikki kiipeilytelineet", to: "/kauppa" },
      ]}
      body={[
        {
          heading: "Pihakiipeilyteline vs. sisäkiipeilyteline — suomalaisen perheen todellinen vertailu",
          paragraphs: [
            "Suomessa on realistinen ulkoleikkisää 5 kuukautta vuodesta (touko–syyskuu). Loput 7 kuukautta piha on joko luminen, jäinen tai märkä. Iso puinen pihakiipeilyteline seisoo silloin lumen peittämänä, ja lapsi kaipaa jotakin muuta kiipeiltäväksi — usein sohvaa tai kirjahyllyä.",
            "Sisäkiipeilyteline ratkaisee tämän: sama karkeamotorinen harjoitus (kiipeäminen, tasapainoilu, vartalonhallinta) 12 kuukautta vuodessa lämpimässä olohuoneessa. Kolmion ja rampin yhdistelmä muodostaa pienen liukumäen — olennaisin puistokokemus ilman ulos menoa.",
          ],
        },
        {
          heading: "Milloin pihateline on silti oikea valinta",
          paragraphs: [
            "Jos sinulla on oma piha, terassi tai puutarha ja tilaa 3×3 m, iso pihakiipeilyteline (esim. Puuilon, Woodstockin tai Lelupisteen mallit) on hieno investointi 3–10-vuotiaalle. Se antaa korkeutta, liukumäen ja usein keinun. Käyttöikä 5–8 vuotta.",
            "Materiaali on kriittinen: painekyllästetty mänty, ruostumaton teräs tai HDPE-muovi. Tavallinen puu vaatii vuosittain uuden lakkakerroksen. Turva-alusta (hakemurske, kumirouhe tai vaahtomuovilaatta) on lain edellyttämä yli 60 cm putousriskin ollessa.",
          ],
        },
        {
          heading: "Miksi sisäteline usein voittaa Suomen ilmastossa",
          paragraphs: [
            "Käyttökuukaudet: sisäteline 12, pihateline 5. Investoinnin €/kk laskettuna sisäteline on 2× halvempi käyttötuntia kohden.",
            "Tila: iso pihateline vaatii 3×3 m tasoitettua pohjaa; taittuva Pikler-sisäteline mahtuu käytössä 80×80 cm ja säilytettynä sängyn alle.",
            "Käyttöikä: koivuvaneri sisällä 8–10 v, säänkestävä puu ulkona 5–8 v (edellyttää huoltoa).",
            "Turvallisuus: sisäteline aikuisen näköpiirissä, pehmeä matto alla. Pihalla lapsi usein yksin ja korkeus suurempi.",
            "Muunneltavuus: Pikler-setti (kolmio + kaari + ramppi) toimii kiipeilytelineenä, majana, luistelurampin harjoituksena ja liukumäkenä. Yksi hankinta korvaa monta.",
          ],
        },
      ]}
      faqs={FAQS}
      cta={{
        heading: "Sisäkiipeilyteline joka toimii 12 kk vuodessa",
        body: "Pikler-kolmio, kaari ja ramppi — sama karkeamotorinen harjoitus kuin pihatelineessä, mutta lämpimässä olohuoneessa. Koivuvaneri, ilmainen toimitus 3–7 päivässä.",
        primaryLabel: "Tutustu Pikler-mallistoon",
        secondaryLabel: "Lue Pikler-opas",
        secondaryTo: "/montessori-koulu/pikler-kiipeilyteline-opas",
      }}
    />
  );
}